import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, ExternalLink } from 'lucide-react';

const FooterImproved = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    'Lavado Carrocería',
    'Lavado de motor',
    'Descontaminación',
    'Encerado',
    'Pulido',
    'Limpieza interior',
  ];

  return (
    <footer className="bg-[#0A1628] border-t border-white/10 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#60A5FA] rounded-full filter blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <motion.div
              className="mb-6"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <img 
                src="/lavaporcar-logo.png" 
                alt="Lavaporcar Logo" 
                className="h-20 w-auto"
              />
            </motion.div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Especialistas en estética automotriz con 3 años de experiencia.
              Pasión por tu vehículo, excelencia en cada detalle.
            </p>
            
            {/* Social Media */}
            <div className="flex space-x-3">
              <motion.a
                href="#"
                whileHover={{ scale: 1.1, y: -3 }}
                className="w-11 h-11 bg-[#60A5FA]/20 rounded-xl flex items-center justify-center hover:bg-[#60A5FA]/30 transition-all duration-300 border border-[#60A5FA]/20"
              >
                <Facebook className="w-5 h-5 text-[#60A5FA]" />
              </motion.a>
              <motion.a
                href="#"
                whileHover={{ scale: 1.1, y: -3 }}
                className="w-11 h-11 bg-[#60A5FA]/20 rounded-xl flex items-center justify-center hover:bg-[#60A5FA]/30 transition-all duration-300 border border-[#60A5FA]/20"
              >
                <Instagram className="w-5 h-5 text-[#60A5FA]" />
              </motion.a>
              <motion.a
                href="#"
                whileHover={{ scale: 1.1, y: -3 }}
                className="w-11 h-11 bg-[#60A5FA]/20 rounded-xl flex items-center justify-center hover:bg-[#60A5FA]/30 transition-all duration-300 border border-[#60A5FA]/20"
              >
                <Twitter className="w-5 h-5 text-[#60A5FA]" />
              </motion.a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Enlaces rápidos</h3>
            <ul className="space-y-3">
              {[
                { label: 'Inicio', href: '#hero' },
                { label: 'Nosotros', href: '#about' },
                { label: 'Servicios', href: '#services' },
                { label: 'Galería', href: '#gallery' },
                { label: 'Testimonios', href: '#testimonials' },
                { label: 'Contacto', href: '#contact' },
              ].map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href} 
                    className="text-white/60 hover:text-[#60A5FA] transition-colors text-sm flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {link.label}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Servicios</h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <a 
                    href="#services" 
                    className="text-white/60 hover:text-[#60A5FA] transition-colors text-sm flex items-center group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {service}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Contacto</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-[#60A5FA] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white/60 text-sm">0958646225</p>
                  <p className="text-white/40 text-xs">WhatsApp disponible</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#60A5FA] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white/60 text-sm">Pichincha, Ecuador</p>
                  <p className="text-white/40 text-xs">Servicio a domicilio</p>
                </div>
              </li>
            </ul>

            {/* CTA Button */}
            <motion.a
              href="https://wa.me/593958646225"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, y: -2 }}
              className="mt-6 inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-[#60A5FA]/30 transition-all duration-300"
            >
              Contáctanos ahora
              <ExternalLink className="w-4 h-4 ml-2" />
            </motion.a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm text-center md:text-left">
            © {currentYear} Lavaporcar. Todos los derechos reservados.
          </p>
          <div className="flex items-center space-x-6">
            <a href="#" className="text-white/50 hover:text-[#60A5FA] text-sm transition-colors">
              Política de privacidad
            </a>
            <a href="#" className="text-white/50 hover:text-[#60A5FA] text-sm transition-colors">
              Términos y condiciones
            </a>
          </div>
        </div>

        {/* Made with love */}
        <div className="mt-6 text-center">
          <p className="text-white/40 text-xs">
            Hecho con pasión por tu vehículo ❤️
          </p>
        </div>
      </div>
    </footer>
  );
};

export default FooterImproved;